import React, { useEffect } from 'react'
import { MODAL_TRIGGERS } from '@/cinematics/modals/registry'
import { ConfigurableModal } from './ConfigurableModal'
import type { IModalScript } from '@/cinematics/modals/types'
import type { IGlobalProps } from '@/types/cinematics'

type ModalVisibilityHandler = (modalId: string, isVisible: boolean) => void

interface IConfigurableModalsProps {
  modals?: IModalScript[]
  stores?: IGlobalProps['stores']
  /** Lets the host hold its own overlays (the spotlight wizard) while a modal is up. */
  onVisibilityChange?: ModalVisibilityHandler
}

interface IModalRunnerProps {
  modal: IModalScript
  stores?: IGlobalProps['stores']
  onVisibilityChange?: ModalVisibilityHandler
}

/** One instance per modal so its trigger hook is called unconditionally
 *  (Rules of Hooks), even though `modals` is a dynamic list. */
const ModalRunner: React.FC<IModalRunnerProps> = ({ modal, stores, onVisibilityChange }) => {
  const trigger = MODAL_TRIGGERS[modal.type]
  const { isVisible, dismiss } = trigger(modal)

  // Visibility is owned by each trigger's own state, so the host can only learn
  // it after the fact. The cleanup reports a modal that unmounts while open.
  useEffect(() => {
    onVisibilityChange?.(modal._id, isVisible)
    return () => onVisibilityChange?.(modal._id, false)
  }, [modal._id, isVisible, onVisibilityChange])

  if (!isVisible) return null

  return <ConfigurableModal modal={modal} stores={stores} onClose={dismiss} />
}

/** Iterates `props.modals`, keeping active entries with a known trigger, and
 *  runs each. Mounted once globally in `Layout`. */
export const ConfigurableModals: React.FC<IConfigurableModalsProps> = ({ modals, stores, onVisibilityChange }) => {
  const activeModals = (modals ?? []).filter(
    (modal) => modal.isActive && MODAL_TRIGGERS[modal.type]
  )

  if (!activeModals.length) return null

  return (
    <>
      {activeModals.map((modal) => (
        <ModalRunner key={modal._id} modal={modal} stores={stores} onVisibilityChange={onVisibilityChange} />
      ))}
    </>
  )
}
